import axios, { AxiosPromise } from "axios";
import { dashboardApiConfig, ops } from "./config";
import { BrandKitDto, BrandKitResourceApi, BrandLogoDto, BrandColorDto, BrandFontsDto, BulkBrandKitRequest } from "../generated/dashboard-client";

const api = new BrandKitResourceApi(dashboardApiConfig);

// DRD attribute -> icon mapping. Hand-written against the BrandKitResource /drd-icons
// endpoints (kept out of the generated client so we don't need a running backend to add it).
export type DrdAttributeType = "ALLERGEN" | "TAG";

export interface BrandKitDrdIconDto {
  attribute: string;
  type: DrdAttributeType;
  iconKey?: string | null;
  iconType?: string | null;
  fill?: string | null;
  altText?: string | null;
  hidden: boolean;
}

const drdIconsUrl = (id: string, attribute?: string) =>
  `${dashboardApiConfig.basePath}/brand-kits/${encodeURIComponent(id)}/drd-icons` +
  (attribute ? `/${encodeURIComponent(attribute)}` : "");

const BrandKitApi = {
  listForTeam(teamId: string): AxiosPromise<BrandKitDto[]> {
    return api.listForTeam(teamId, ops());
  },
  get(id: string): AxiosPromise<BrandKitDto> {
    return api.get2(id, ops());
  },
  create(dto: BrandKitDto): AxiosPromise<BrandKitDto> {
    return api.createBrandKit(dto, ops());
  },
  createBulk(request: BulkBrandKitRequest): AxiosPromise<BrandKitDto[]> {
    return api.createBulkBrandKits(request, ops());
  },
  update(id: string, dto: BrandKitDto): AxiosPromise<BrandKitDto> {
    return api.updateBrandKit(id, dto, ops());
  },
  delete(id: string): AxiosPromise<void> {
    return api.deleteBrandKit(id, ops());
  },
  assignLocation(id: string, locationId: number): AxiosPromise<BrandKitDto> {
    return api.assignLocation(id, locationId, ops());
  },
  unassignLocation(id: string, locationId: number): AxiosPromise<BrandKitDto> {
    return api.unassignLocation(id, locationId, ops());
  },
  getLogos(id: string): AxiosPromise<BrandLogoDto[]> {
    return api.getLogos(id, ops());
  },
  getColors(id: string): AxiosPromise<BrandColorDto[]> {
    return api.getColors(id, ops());
  },
  addColor(id: string, colorCode: string, name?: string): AxiosPromise<BrandColorDto[]> {
    return api.addColor(id, { colorCode, name }, ops());
  },
  updateColor(id: string, oldColorCode: string, newColorCode: string, name?: string): AxiosPromise<BrandColorDto[]> {
    return api.updateColor(id, oldColorCode, { colorCode: newColorCode, name }, ops());
  },
  deleteColor(id: string, colorCode: string): AxiosPromise<BrandColorDto[]> {
    return api.deleteColor(id, colorCode, ops());
  },
  getFonts(id: string): AxiosPromise<BrandFontsDto> {
    return api.getFonts1(id, ops());
  },
  saveFonts(id: string, data: BrandFontsDto): AxiosPromise<BrandFontsDto> {
    return api.saveFonts(id, data, ops());
  },
  getDrdIcons(id: string): AxiosPromise<BrandKitDrdIconDto[]> {
    return axios.get(drdIconsUrl(id), ops());
  },
  upsertDrdIcon(id: string, attribute: string, dto: BrandKitDrdIconDto): AxiosPromise<BrandKitDrdIconDto> {
    return axios.put(drdIconsUrl(id, attribute), dto, ops());
  },
  deleteDrdIcon(id: string, attribute: string): AxiosPromise<void> {
    return axios.delete(drdIconsUrl(id, attribute), ops());
  },
};

export default BrandKitApi;
